import React, { useContext, useState } from "react";
import {
  Modal,
  Container,
  Title,
  Text,
  Stack,
  Button,
  Group,
  ActionIcon,
  Box,
  Card,
  Divider,
  Alert,
  Loader,
  TextInput,
  Select,
  Checkbox,
} from "@mantine/core";
import {
  IconX,
  IconCreditCard,
  IconLock,
  IconShield,
  IconCheck,
  IconAlertCircle,
  IconBrandStripe,
  IconMail,
  IconPhone,
  IconMapPin,
  IconBuilding,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import "./styles.scss";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getCurrencySymbol,
} from "../../Lib/methods";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/endpoints";
import { PlanProps, StudentProps } from "../../Lib/types";
import { AppContext } from "../Dashboard";
import MegaLoader from "../../Components/MegaLoader";
import { useNavigate } from "react-router-dom";
import { AllFatherContext } from "../../App";
import { HttpSuccessCodes, HttpSuccessCodesObj } from "../../Lib/data";

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_your_key_here"
);

interface StripePaymentModalProps {
  opened: boolean;
  onClose: () => void;
  planDetails?: PlanProps;
  customerInfo?: {
    name: string;
    email: string;
    phone?: string;
  };
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
      backgroundColor: "transparent",
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const CheckoutForm: React.FC<{
  onSuccess: () => void;
  onError: (error: string) => void;
  planDetails: PlanProps;
  profile: StudentProps;
  onClose: () => void;
}> = ({ onSuccess, onError, planDetails, profile, onClose }) => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const allFatherContext = useContext(AllFatherContext);
  const elements = useElements();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [billingInfo, setBillingInfo] = useState({
    firstName: profile?.name?.split(" ")[0] || "",
    lastName: profile?.name?.split(" ").slice(1).join(" ") || "",
    email: profile?.email || "",

    address: "",
    city: "",
    country: "",
    postalCode: "",
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!agreedToTerms) {
      onError("Please agree to the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${billingInfo.firstName} ${billingInfo.lastName}`,
          email: billingInfo.email,
        },
      });

      if (error) {
        throw new Error(error.message);
      } else {
        const SubscribeToPlan = async () => {
          setLoading(true);
          const r = await PerformRequest({
            method: Endpoints.GetCreateStripeSubscription().method,
            route: Endpoints.GetCreateStripeSubscription().route,
            data: {
              paymentMethodId: paymentMethod?.id,
              planID: planDetails?.id,
              billingInterval: "month",
              priceId: planDetails.stripePriceID,
            },
            callback: () => {
              setLoading(false);
            },
          });
          setLoading(false);
          if (r) {
            if (r.status === HttpSuccessCodesObj.Created) {
              DefaultSuccessNotification("Payment successful");
              navigate("/d/courses");
            }
          }
        };

        await SubscribeToPlan();
      }

      // Simulate payment processing
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Call the success callback
      onSuccess();
    } catch (error: any) {
      onError(error.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const totals = planDetails.price;

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="lg">
        {/* Order Summary */}
        <Card withBorder p="md" radius="md" className="order-summary">
          <Group justify="space-between" mb="xs">
            <Text fw={600}>{planDetails.name}</Text>
            <Text fw={600}>
              {getCurrencySymbol(allFatherContext?.locationInformation?.currency)}
              {planDetails.price}
            </Text>
          </Group>

          <Text size="sm" c="dimmed" mb="xs">
            Monthly subscription
          </Text>

          <Divider my="sm" />

          <Group justify="space-between">
            <Text fw={700} size="lg">
              Total
            </Text>
            <Text fw={700} size="lg" c="violet">
              {getCurrencySymbol(allFatherContext?.locationInformation?.currency)}
              {totals.toFixed(2)}
            </Text>
          </Group>

          <Alert
            icon={<IconCheck size={16} />}
            color="blue"
            variant="light"
            mt="md"
            title="Subscription Details"
          >
            <Text size="sm">
              • This is a monthly subscription that will be billed automatically
              <br />
              • You can cancel anytime from your account settings
              <br />• Access to all courses included in this plan
            </Text>
          </Alert>
        </Card>

        {/* Billing Information */}
        <Stack gap="md">
          <Title order={4}>Billing Information</Title>

          <Group gap="md">
            <TextInput
              disabled
              label="First Name"
              placeholder="John"
              value={billingInfo.firstName}
              onChange={(e) => setBillingInfo({ ...billingInfo, firstName: e.target.value })}
              required
              style={{ flex: 1 }}
            />
            <TextInput
              disabled
              label="Last Name"
              placeholder="Doe"
              value={billingInfo.lastName}
              onChange={(e) => setBillingInfo({ ...billingInfo, lastName: e.target.value })}
              required
              style={{ flex: 1 }}
            />
          </Group>

          <TextInput
            disabled
            label="Email Address"
            placeholder="Enter email..."
            value={billingInfo.email}
            onChange={(e) => setBillingInfo({ ...billingInfo, email: e.target.value })}
            leftSection={<IconMail size={16} />}
            required
          />
        </Stack>

        {/* Payment Method */}
        <Stack gap="md">
          <Title order={4}>Payment Method</Title>

          <Card withBorder p="md" radius="md" className="payment-card">
            <Group gap="sm" mb="md">
              <IconBrandStripe size={20} color="#6772e5" />
              <Text fw={600}>Credit or Debit Card</Text>
            </Group>

            <Box className="card-element-container">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </Box>
          </Card>
        </Stack>

        {/* Terms */}
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          checked={agreedToTerms}
          onChange={(event) => setAgreedToTerms(event.currentTarget.checked)}
          required
        />

        {/* Submit Button */}
        <Button
          type="submit"
          loading={loading}
          disabled={!agreedToTerms}
          size="lg"
          fullWidth
          className="pay-button"
        >
          {loading
            ? "Processing..."
            : `Subscribe for ${getCurrencySymbol(allFatherContext?.locationInformation?.currency)}
            ${totals.toFixed(2)}/month`}
        </Button>
      </Stack>
    </form>
  );
};

const StripePaymentModal: React.FC<StripePaymentModalProps> = ({
  opened,
  onClose,
  planDetails,
  customerInfo,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const [paymentStatus, setPaymentStatus] = useState<"idle" | "processing" | "success" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");

  const handleSuccess = () => {
    setPaymentStatus("success");
    onSuccess?.();
  };

  const handleError = (error: string) => {
    setPaymentStatus("error");
    setErrorMessage(error);
    onError?.(error);
  };

  const handleClose = () => {
    setPaymentStatus("idle");
    setErrorMessage("");
    onClose();
  };
  const context = useContext(AppContext);
  return (
    <>
      {context && context.profile ? (
        <Modal
          opened={opened}
          onClose={handleClose}
          size="md"
          centered
          withCloseButton={false}
          className="stripe-payment-modal"
          overlayProps={{
            blur: 2,
            opacity: 0.6,
          }}
        >
          <Container size="sm" py="xl">
            {/* Header */}
            <Group justify="space-between" align="center" mb="xl">
              <Group gap="sm">
                <IconBrandStripe size={24} color="#6772e5" />
                <div>
                  <Title order={3}>Checkout</Title>
                  <Text c="dimmed" size="sm">
                    Secure payment powered by Stripe
                  </Text>
                </div>
              </Group>
              <ActionIcon variant="subtle" size="lg" onClick={handleClose} className="close-button">
                <IconX size={20} />
              </ActionIcon>
            </Group>

            {/* Security Badge */}
            <Alert
              icon={<IconShield size="1rem" />}
              title="Secure Payment"
              color="green"
              mb="xl"
              className="security-alert"
            >
              Your payment information is encrypted and secure.
            </Alert>

            {/* Main Content */}

            <Elements stripe={stripePromise}>
              <CheckoutForm
                onSuccess={onSuccess || (() => {})}
                onError={onError || ((error: string) => {})}
                planDetails={planDetails as PlanProps}
                profile={context.profile}
                onClose={handleClose}
              />
            </Elements>

            {/* Error Alert */}
            {paymentStatus === "error" && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                title="Payment Failed"
                color="red"
                mt="md"
                withCloseButton
                onClose={() => setPaymentStatus("idle")}
              >
                {errorMessage}
              </Alert>
            )}
          </Container>
        </Modal>
      ) : (
        <MegaLoader />
      )}
    </>
  );
};

export default StripePaymentModal;
